
import React from 'react';
import { Link } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { NavLink } from './NavLink';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { User } from '@supabase/supabase-js';

interface DesktopNavProps {
  user: User | null;
  isDriver: boolean;
  signOut: () => void;
}

const DesktopNav: React.FC<DesktopNavProps> = ({ user, isDriver, signOut }) => {
  return (
    <div className="hidden md:flex md:items-center md:space-x-6">
      <NavLink to="/" className="hover:text-primary transition-colors duration-200">Home</NavLink>
      <NavLink to="/how-it-works" className="hover:text-primary transition-colors duration-200">How It Works</NavLink>
      <NavLink to="/safety" className="hover:text-primary transition-colors duration-200">Safety</NavLink>
      <NavLink to="/about-us" className="hover:text-primary transition-colors duration-200">About Us</NavLink>
      {/* Reachable from every marketing page, not just the home hero. A
          signed-out visitor can finish the demo and cannot finish a booking,
          so it belongs beside the pages they are already reading. */}
      {!user && (
        <NavLink to="/demo" className="font-medium text-primary hover:opacity-80 transition-opacity duration-200">Demo</NavLink>
      )}
      
      {user ? (
        <>
          <NavLink to="/rides" className="hover:text-primary transition-colors duration-200">My Rides</NavLink>
          {!isDriver && (
            <NavLink to="/my-drivers" className="hover:text-primary transition-colors duration-200">My Drivers</NavLink>
          )}
          {isDriver && (
            <NavLink to="/driver-dashboard" className="hover:text-primary transition-colors duration-200">Driver Dashboard</NavLink>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="relative h-10 w-10 rounded-full overflow-hidden border-2 border-primary/10 hover:border-primary/30 transition-all duration-200">
                <Avatar className="h-9 w-9">
                  <AvatarImage src={user.user_metadata?.avatar_url || ""} alt={user.user_metadata?.name || user.email} />
                  <AvatarFallback className="bg-primary/10 text-primary font-medium">
                    {user.user_metadata?.name ? user.user_metadata.name.charAt(0).toUpperCase() : user.email?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 p-2 rounded-xl shadow-lg border border-border/50 animate-in fade-in-50 zoom-in-95 duration-100">
              <DropdownMenuLabel className="font-semibold text-base px-3 py-2">My Account</DropdownMenuLabel>
              <DropdownMenuSeparator className="my-1.5" />
              <DropdownMenuItem asChild className="rounded-lg focus:bg-primary/10 focus:text-primary px-3 py-2.5 cursor-pointer">
                <Link to="/profile" className="flex items-center">
                  <span>Profile</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="rounded-lg focus:bg-primary/10 focus:text-primary px-3 py-2.5 cursor-pointer">
                <Link to="/rides" className="flex items-center">
                  <span>Ride History</span>
                </Link>
              </DropdownMenuItem>
              {isDriver ? (
                <DropdownMenuItem asChild className="rounded-lg focus:bg-primary/10 focus:text-primary px-3 py-2.5 cursor-pointer">
                  <Link to="/driver-dashboard" className="flex items-center">
                    <span>Driver Dashboard</span>
                  </Link>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem asChild className="rounded-lg focus:bg-primary/10 focus:text-primary px-3 py-2.5 cursor-pointer">
                  <Link to="/driver-signup" className="flex items-center">
                    <span>Become a Driver</span>
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="my-1.5" />
              <DropdownMenuItem onClick={signOut} className="rounded-lg focus:bg-destructive/10 hover:bg-destructive/10 focus:text-destructive hover:text-destructive transition-colors px-3 py-2.5 cursor-pointer">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      ) : (
        <>
          <NavLink to="/driver-signup" className="hover:text-primary transition-colors duration-200">Become a Driver</NavLink>
          <Button asChild className="rounded-xl bg-gradient-to-r from-primary to-secondary hover:opacity-90 transition-opacity shadow-md hover:shadow-lg">
            <Link to="/auth">Sign In</Link>
          </Button>
        </>
      )}
    </div>
  );
};

export default DesktopNav;
